/**
 * 标准配合求解状态聚合策略。
 *
 * 文件职责：
 * 1. 将单条 Mate 诊断聚合成求解器总体状态；
 * 2. 隔离 PropertyManager 临时 Mate 的状态语义；
 * 3. 避免求解主流程直接耦合 UI 文案或文档投影逻辑。
 */

import { MateSolveStatus } from '../definition';
import type { AssemblyMateSolveDiagnostic } from './StandardMateSolverTypes';

/**
 * 解析 PropertyManager 草稿 Mate 的阻塞状态。
 *
 * @param diagnostics 单条诊断集合。
 * @param transientMateIds PropertyManager 中尚未提交的临时 Mate ID。
 * @returns 临时 Mate 造成的阻塞状态；没有临时阻塞时返回 null。
 */
function resolveTransientBlockingStatus(
  diagnostics: readonly AssemblyMateSolveDiagnostic[],
  transientMateIds: ReadonlySet<string>,
): MateSolveStatus | null {
  if (transientMateIds.size === 0) {
    return null;
  }

  const transientDiagnostics = diagnostics.filter((diagnostic) => (
    transientMateIds.has(diagnostic.mateId)
    && diagnostic.status !== MateSolveStatus.Solved
    && diagnostic.status !== MateSolveStatus.NotRun
  ));
  if (transientDiagnostics.length === 0) {
    return null;
  }

  /**
   * PropertyManager 里的草稿 Mate 还没有写入装配体文档。
   * 它和已有 Mate 冲突时必须阻止确认，但不应把文档整体状态提前标成过定义。
   */
  if (transientDiagnostics.some((item) => item.status === MateSolveStatus.MissingReference)) {
    return MateSolveStatus.MissingReference;
  }
  if (transientDiagnostics.some((item) => item.status === MateSolveStatus.UnsupportedGeometry)) {
    return MateSolveStatus.UnsupportedGeometry;
  }
  return MateSolveStatus.Unsolved;
}

/**
 * 解析 PropertyManager 草稿 Mate 的整体预览状态。
 *
 * 设计说明：
 * SOLIDWORKS 的 Mate PropertyManager 当前正在处理“尚未提交的草稿 Mate”。同一次预览求解中保留
 * 已提交 Mate 的诊断，是为了让设计树和后续提交链路仍能定位历史错误；但只要这些错误没有归因到
 * 临时 Mate，就不能把当前草稿顶部状态污染成“配合过定义”。
 *
 * @param diagnostics 单条诊断集合。
 * @param transientMateIds PropertyManager 中尚未提交的临时 Mate ID。
 * @returns 草稿 Mate 的整体状态；没有草稿 Mate 时返回 null。
 */
function resolveTransientOverallStatus(
  diagnostics: readonly AssemblyMateSolveDiagnostic[],
  transientMateIds: ReadonlySet<string>,
): MateSolveStatus | null {
  if (transientMateIds.size === 0) {
    return null;
  }

  const transientBlockingStatus = resolveTransientBlockingStatus(diagnostics, transientMateIds);
  if (transientBlockingStatus) {
    return transientBlockingStatus;
  }

  const hasTransientDiagnostic = diagnostics.some((diagnostic) => (
    transientMateIds.has(diagnostic.mateId)
  ));
  return hasTransientDiagnostic ? MateSolveStatus.Solved : null;
}

/**
 * 聚合求解状态。
 *
 * 设计说明：
 * SOLIDWORKS MateXpert 会把“不满足”作为错误，把“过定义”作为警告；因此当同一轮诊断同时包含
 * unsolved/missing/unsupported 与 overDefined 时，总体状态必须优先暴露阻断性错误。
 *
 * @param diagnostics 单条诊断集合。
 * @param transientMateIds PropertyManager 中尚未提交的临时 Mate ID。
 * @returns 总体状态。
 */
export function resolveStandardMateSolverOverallStatus(
  diagnostics: readonly AssemblyMateSolveDiagnostic[],
  transientMateIds: ReadonlySet<string>,
): MateSolveStatus {
  const transientOverallStatus = resolveTransientOverallStatus(diagnostics, transientMateIds);
  if (transientOverallStatus) {
    return transientOverallStatus;
  }

  if (diagnostics.some((item) => item.status === MateSolveStatus.MissingReference)) {
    return MateSolveStatus.MissingReference;
  }
  if (diagnostics.some((item) => item.status === MateSolveStatus.UnsupportedGeometry)) {
    return MateSolveStatus.UnsupportedGeometry;
  }
  if (diagnostics.some((item) => item.status === MateSolveStatus.Unsolved)) {
    return MateSolveStatus.Unsolved;
  }
  if (diagnostics.some((item) => item.status === MateSolveStatus.OverDefined)) {
    return MateSolveStatus.OverDefined;
  }
  return MateSolveStatus.Solved;
}
